import { uniqBy } from 'lodash';
import Tags from '../collections/Tags';
import Tag from '../models/Tag';
import { baseApi } from 'core/api';

const state = () => ({
    _items: null,
    _pagination: null,
    _parameters: {
        page: 1,
        sortBy: 'created_at',
        sortOrder: 'desc',
        search: null,
        length: 10,
    },
    _editing: null,
    _deleting: null,
    _bulk_deleting: null,
    _loading: false,
    _bulk_all: false,
    _search: [],
});

const getters = {
    tags: (state) => state._items,
    isLoadingTags: (state) => state._loading,
    tagsEditing: (state) => state._editing,
    tagDeleting: (state) => state._deleting,
    tagBulkDeleting: (state) => state._bulk_deleting,
    tagsParameters: (state) => state._parameters,
    tagsPagination: (state) => state._pagination,
    tagBulkAll: (state) => state._bulk_all,
};

const actions = {
    resetTags() {
        this._setTagReset();
    },

    resetTagsParameters(data) {
        this._setTagResetParameters(data);
    },

    async tagsLoad({ retry, updateCollection = true, params = {} } = {}) {
        if (this._loading) return;

        this._setTagsLoading(true);

        params = {
            ...this._parameters,
            ...params,
        };

        if (retry === true) {
            this._setTagsRetryLoading();
        }

        return baseApi
            .get('core/tags', { params: params })
            .then((response) => {
                const items = new Tags(response.data.data).models;
                const data = {
                    ...response.data,
                    data: items,
                };

                if (updateCollection) {
                    this._setTagsLoaded(data);
                }

                return items;
            })
            .catch(() => {
                this._setTagsErrorLoading();
            })
            .finally(() => {
                this._setTagsLoading(false);
            });
    },

    tagsSearch(term) {
        let url = 'core/tags/search';

        if (this._pagination.next_page_url) {
            url = this._pagination.next_page_url;
        }

        return baseApi
            .get(url, { params: { term: term } })
            .then((response) => {
                this._setTagsSearchLoaded({
                    term: term,
                    data: response.data,
                });

                return response;
            })
            .catch((errors) => {
                this._setTagsSearchError();

                return errors;
            });
    },

    tagsClearSearch() {
        this._setTagsSearchCleared();
        this.tagsLoad();
    },

    createTags({ form, useAxios = false, data = {} }) {
        if (useAxios) {
            return baseApi.post('core/tags', data).then((response) => {
                response.data.contacts_count = 0;
                this._setTagCreated(new Tag(response.data));

                return response.data;
            });
        }

        return form.post('core/tags').then((response) => {
            response.data.contacts_count = 0;
            this._setTagCreated(new Tag(response.data));

            return response.data;
        });
    },

    tagsUpdate({ id, form }) {
        return form.put(`core/tags/${id}`).then((response) => {
            this._setTagUpdated(response.data);

            return response;
        });
    },

    tagDelete({ id, form }) {
        return form.delete(`core/tags/${id}`).then((response) => {
            this._setTagDeleted(id);

            return response;
        });
    },

    tagsBulkDelete(form) {
        return form
            .delete('core/tags')
            .then((response) => {
                this._setTagsBulkDeleted(form);

                return response;
            })
            .catch((err) => err);
    },

    updateTagsByIdContactById({ contactId, tagId }) {
        return baseApi.put(`core/tags/contact/${contactId}/${tagId}`).then((response) => {
            return response.data;
        });
    },

    deleteTagsByIdContactById({ contactId, tagId }) {
        return baseApi.delete(`core/tags/contact/${contactId}/${tagId}`).then((response) => {
            return response.data;
        });
    },

    tagCreated(tag) {
        this._setTagCreated(tag);
    },

    tagUpdated(tag) {
        this._setTagUpdated(tag);
    },

    tagEditing(tag) {
        this._setTagEditing(tag);
    },

    tagsDeleting(tag) {
        this._setTagDeleting(tag);
    },

    tagsBulkDeleting(tags) {
        this._setTagsBulkDeleting(tags);
    },

    tagDeleted({ id }) {
        this._setTagDeleted(id);
    },

    tagsBulkDeleted(ids) {
        this._setTagsBulkDeleted({ ids });
    },

    tagsUpdateSort(sort) {
        this._setTagsUpdateSort(sort);
        this.tagsLoad();
    },

    tagsUpdateSearch(search) {
        this._setTagsUpdateSearch(search);
        this.tagsLoad();
    },

    tagsUpdatePage(page) {
        this._setTagsUpdatePage(page);
        this.tagsLoad();
    },

    tagsUpdateLength(page) {
        this._setTagsUpdateLength(page);
        this.tagsLoad();
    },

    tagsCustomUpdateLengthSelector({ page, data }) {
        this._setTagsUpdateLength(page);
        this.tagsUpdatePage(data);
    },

    tagBulkCheck(id) {
        this._setTagsBulkCheck(id);
    },

    tagsBulkAll(bulkAllStatus) {
        this._setTagsBulkAll(bulkAllStatus);
    },

    // Private methods for all operations
    _setTagsLoading(status) {
        this._loading = status;
    },

    _setTagResetParameters(data) {
        this._parameters = {
            page: 1,
            sortBy: 'created_at',
            sortOrder: 'desc',
            search: null,
            length: 100,
            ...data,
        };
    },

    _setTagReset() {
        this._items = null;
    },

    _setTagCreated(data) {
        if (this._items === null) {
            this._items = [data];
        } else {
            this._items.unshift(data);

            if (this._items.length > this._parameters.length) {
                this._items.pop();
            } else {
                this._pagination.to = this._items.length;
            }

            if (this._pagination.from === null) {
                this._pagination.from = 1;
            }

            ++this._pagination.total;
        }
    },

    _setTagUpdated(data) {
        if (this._items === null) {
            return;
        }

        this._items = this._items.map((o) => {
            if (o.id === data.id) {
                return data;
            } else {
                return o;
            }
        });

        if (this._search.results) {
            this._search.results = this._search.results.map((o) => {
                if (o.id === data.id) {
                    return data;
                } else {
                    return o;
                }
            });
        }
    },

    _setTagDeleted(id) {
        if (this._items === null) {
            return;
        }

        this._items = this._items.filter((o) => o.id !== id);
        --this._pagination.total;

        if (this._items.length < this._parameters.length) {
            this._pagination.to = this._items.length;
        }

        if (this._search.results) {
            this._search.results = this._search.results.filter((o) => o.id !== id);
        }
    },

    _setTagsBulkDeleted(form) {
        const { ids, delete_all } = form;

        if (!this._items) return;

        this._items = this._items.filter((o) => {
            return ids.indexOf(o.id) === -1;
        });

        this._pagination.total = this._pagination.total - ids.length;

        if (this._search.results) {
            this._search.results = this._search.results.filter((o) => {
                return ids.indexOf(o.id) === -1;
            });
        }

        if (delete_all) {
            this._items = [];
            this._search.results = [];
        }
    },

    _setTagsLoaded(data) {
        this._items = data.data;
        this._pagination = {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
            more: data.next_page_url,
        };
    },

    _setTagsRetryLoading() {
        this._items = null;
        this._pagination.total = 0;
    },

    _setTagsErrorLoading() {
        this._items = false;
        if (this._pagination) {
            this._pagination.total = 0;
        }
    },

    _setTagsSearchLoaded({ term, data }) {
        if (this._search.items === null) {
            this._search.items = data.data;
        } else {
            this._search.items = [];

            for (let i in data.data) {
                this._search.items.push(data.data[i]);
            }

            this._search.items = uniqBy(this._search.items, 'id');
        }

        this._search.more = data.next_page_url;
        this._search.total = data.total;
        this._search.term = term;
    },

    _setTagsSearchError() {
        this._search = {
            items: null,
            more: null,
            total: 0,
            term: null,
        };
    },

    _setTagsSearchCleared() {
        this._parameters.search = null;
    },

    _setTagsUpdateSearch(data) {
        this._parameters.page = 1;
        this._parameters.search = data;
    },

    _setTagsUpdatePage(data) {
        this._parameters.page = data;
    },

    _setTagsUpdateLength(data) {
        this._parameters.length = data;
    },

    _setTagEditing(data) {
        this._editing = data;
    },

    _setTagDeleting(data) {
        this._deleting = data;
        this._bulk_deleting = null;
    },

    _setTagsBulkDeleting(data) {
        this._bulk_deleting = data;
    },

    _setTagsUpdateSort(data) {
        this._parameters.sortBy = data.sortBy;
        this._parameters.sortOrder = data.sortOrder;
    },

    _setTagsBulkCheck(id) {
        this._items = this._items.map((item) => {
            if (item.id === id) {
                item.checked = !item.checked;
            }
            return item;
        });
    },

    _setTagsBulkAll(bulkAllStatus) {
        this._bulk_all = bulkAllStatus;
    },
};

export default {
    state,
    getters,
    actions,
};
